import React, { useState, useEffect } from 'react';
import { AppSelect } from '../../../../components/common/select';
import ServiceSection from '../LeadsForm/ServiceSection';
import DocumentSection from '../LeadDetail/DocumentSection';
import Alert from '../../../../components/Alert/Alert';
import ConfirmationModal from '../../../../components/common/ConfirmationModal';
import api from '../../../../api';
import { useNavigate } from 'react-router-dom';
import { getSalesPackCidCode } from '../../../../utils/auditSalesPackDocuments';


const DetailTab = ({ lead, leadDetail, loading, onUpdateServices, onRefreshLeadDetail }) => {
  // Follow Up / Activity history comes from the lead detail response. Prefer leadDetail: that is
  // what onRefreshLeadDetail refetches, so a task saved on the Task tab shows up without a reload.
  const history = leadDetail || lead;
  const [formData, setFormData] = useState({
    services: []
  });
  const [tempServices, setTempServices] = useState([]);
  const [isEditingServices, setIsEditingServices] = useState(false);
  const [options, setOptions] = useState({ servicesOptions: [] });
  const [standardsOptionsMap, setStandardsOptionsMap] = useState({});
  const [isccScopeOptions, setIsccScopeOptions] = useState([]);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const navigate = useNavigate();

  const [isSaving, setIsSaving] = useState(false);
  
  // Confirmation modal states
  const [deleteServiceModal, setDeleteServiceModal] = useState({ 
    show: false, 
    serviceIndex: null, 
    serviceId: null 
  });
  const [deleteFileModal, setDeleteFileModal] = useState({
    show: false,
    fileName: null
  });
  const [isDeletingFile, setIsDeletingFile] = useState(false);

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    // Auto-hide alert after 5 seconds
    setTimeout(() => {
      setAlert({ show: false, type: '', message: '' });
    }, 5000);
  };

  const hideAlert = () => {
    setAlert({ show: false, type: '', message: '' });
  };

  // Check if a service is ISCC or Sustainability
  const isIsccService = (serviceId) => {
    // options.servicesOptions values are always strings (String(opt.id), see fetchServicesOptions
    // below), but serviceId passed in here (leadDetail.services[].serviceId) is a number from the
    // backend -- strict === between a string and number is always false, so this silently returned
    // undefined for every ISCC service, making the scope-parsing logic above treat it as a regular
    // string-scope service (wrong shape fed into the array-based Scope multi-select on Edit).
    // isTrainingService right below already does this coercion correctly.
    const service = options.servicesOptions?.find(opt => opt.value === String(serviceId));
    return service?.label?.toLowerCase().includes('iscc') ||
           service?.code?.toLowerCase().includes('iscc') ||
           service?.label?.toLowerCase().includes('sustainability') ||
           service?.code?.toLowerCase().includes('sustainability');
  };

  // Check if a service is Training
  const isTrainingService = (serviceId) => {
    const service = options.servicesOptions?.find(opt => opt.value === String(serviceId));
    return service?.label?.toLowerCase().includes('training');
  };

  // Add fallback ISCC scope options in case API fails
  const getFallbackIsccScopeOptions = () => {
    return [
      { value: 'fallback-1', label: 'ISCC Scope 1 - Fallback Option' },
      { value: 'fallback-2', label: 'ISCC Scope 2 - Fallback Option' },
      { value: 'fallback-3', label: 'ISCC Scope 3 - Fallback Option' }
    ];
  };

  // Fetch ISCC scope options
  const fetchIsccScopeOptions = async () => {
    try {

      // Try to get ALL ISCC scope data first (non-paginated)
      let response;
      try {
        response = await api.get('/md/iscc-scope/all');
      } catch (allEndpointError) {

        // If /all endpoint fails, try the main paginated endpoint with large page size
        try {
          response = await api.get('/md/iscc-scope?page=0&size=1000&search=');
        } catch (endpointError) {

          // Try alternative endpoints
          const alternativeEndpoints = [
            '/api/md/iscc-scope/all',
            '/crm/iscc-scope/all',
            '/master/iscc-scope/all',
            '/md/iscc-scope/all',
            '/api/md/iscc-scope?page=0&size=1000&search=',
            '/crm/iscc-scope?page=0&size=1000&search=',
            '/master/iscc-scope?page=0&size=1000&search=',
            '/md/scope'
          ];

          for (const endpoint of alternativeEndpoints) {
            try {
              response = await api.get(endpoint);
              break;
            } catch (altError) {
              // Continue to next endpoint
            }
          }

          if (!response) {
            throw new Error('All endpoints failed');
          }
        }
      }


      // Handle different response structures
      let scopeData = [];
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          scopeData = response.data;
        } else if (response.data.content && Array.isArray(response.data.content)) {
          scopeData = response.data.content;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          scopeData = response.data.data;
        } else if (response.data.items && Array.isArray(response.data.items)) {
          scopeData = response.data.items;
        } else if (response.data.records && Array.isArray(response.data.records)) {
          scopeData = response.data.records;
        } else {
          scopeData = [];
        }
      }


      if (scopeData.length > 0) {
        const formattedOptions = scopeData.map(scope => ({
          value: scope.id || scope.value || scope.code,
          label: scope.code && scope.name ? `${scope.code} - ${scope.name}` : (scope.label || scope.name || 'Unknown Scope')
        }));
        setIsccScopeOptions(formattedOptions);
      } else {
        setIsccScopeOptions(getFallbackIsccScopeOptions());
      }
    } catch (error) {
      setIsccScopeOptions(getFallbackIsccScopeOptions());
    }
  };

  // useEffect(() => {
  //   alert(JSON.stringify(lead))
  // } , [lead]);
  // Initialize services data when leadDetail changes
  useEffect(() => {
    if (leadDetail?.services?.length > 0 && options.servicesOptions.length > 0) {
      const mappedServices = leadDetail.services.map(service => {
          // Gunakan serviceId jika ada, fallback ke mapping nama->ID
          let serviceId = service.serviceId;
          if (!serviceId && service.name) {
            const found = options.servicesOptions.find(opt => opt.label === service.name);
            serviceId = found ? found.value : '';
          }
          
          // Handle scope parsing for all services
          let parsedScope = service.scope || '';
          const isIscc = isIsccService(serviceId);
          
          if (isIscc) {
            // For ISCC services, scope should be an array
            if (Array.isArray(service.scope)) {
              parsedScope = service.scope;
            } else if (typeof service.scope === 'string' && service.scope.startsWith('[') && service.scope.endsWith(']')) {
              // Handle stringified JSON array
              try {
                parsedScope = JSON.parse(service.scope);
              } catch (e) {
                console.warn(`Failed to parse ISCC scope JSON:`, service.scope);
                parsedScope = service.scope ? [service.scope] : [];
              }
            } else {
              parsedScope = service.scope ? [service.scope] : [];
            }
          } else {
            // For non-ISCC services, scope should be a string
            if (Array.isArray(service.scope)) {
              parsedScope = service.scope.join(', ');
            } else if (typeof service.scope === 'string' && service.scope.startsWith('[') && service.scope.endsWith(']')) {
              // Handle stringified JSON array for non-ISCC
              try {
                const parsed = JSON.parse(service.scope);
                parsedScope = Array.isArray(parsed) ? parsed.join(', ') : parsed;
              } catch (e) {
                parsedScope = service.scope;
              }
            } else {
              parsedScope = service.scope;
            }
          }
          
          // standards is now [{standardId, scope}, ...] from the backend.
          const standardsRaw = Array.isArray(service.standards) ? service.standards : [];
          const isPerRowShape = standardsRaw.length > 0 && standardsRaw[0] !== null && typeof standardsRaw[0] === 'object' && 'standardId' in standardsRaw[0];
          const standardIds = isPerRowShape
            ? standardsRaw.map(s => String(s.standardId)).filter(Boolean)
            : (Array.isArray(service.standardIds) ? service.standardIds : standardsRaw).map(s => String(s)).filter(Boolean);

          // Build per-row scope array
          let scopeRows;
          if (isPerRowShape) {
            scopeRows = standardsRaw.map(s => {
              const raw = s.scope;
              if (!raw) return isIscc ? [] : '';
              if (isIscc) {
                if (Array.isArray(raw)) return raw;
                if (typeof raw === 'string' && raw.startsWith('[')) {
                  try { return JSON.parse(raw); } catch (e) { return []; }
                }
                return [];
              }
              if (typeof raw === 'string' && raw.startsWith('[')) {
                try {
                  const parsed = JSON.parse(raw);
                  return Array.isArray(parsed) ? parsed.join(', ') : parsed;
                } catch (e) { return raw; }
              }
              return raw;
            });
            if (scopeRows.length === 0) scopeRows = [isIscc ? [] : ''];

            // Proposal edits only update the lead service's top-level scope
            // (see EditProposal PUT /lead-services/{id}), not per-standard rows.
            // Fall back to it when every per-row scope is empty so converted
            // leads still show the scope set from the Proposal side.
            const isRowEmpty = (row) => isIscc ? (!Array.isArray(row) || row.length === 0) : !row;
            const hasParsedScope = isIscc ? (Array.isArray(parsedScope) && parsedScope.length > 0) : !!parsedScope;
            if (hasParsedScope && scopeRows.every(isRowEmpty)) {
              scopeRows[0] = parsedScope;
            }
          } else {
            // Legacy: single scope — put on first row
            const rowCount = Math.max(standardIds.length, 1);
            scopeRows = Array.from({ length: rowCount }, (_, rowIdx) => {
              if (rowIdx !== 0) return isIscc ? [] : '';
              return parsedScope;
            });
          }

          return {
            id: service.id, // LeadService entity ID (critical for updates!)
            serviceId: service.serviceId, // Service type ID
            ...service,
            name: serviceId ? String(serviceId) : '',
            standards: standardIds,
            scope: scopeRows,
            documents: {
              cid1: service.cid1FileName
                ? { name: service.cid1FileName, url: getFileUrl(service.cid1FileName) }
                : undefined,
              cid2: service.cid2FileName
                ? { name: service.cid2FileName, url: getFileUrl(service.cid2FileName) }
                : undefined,
              other: service.otherFilesNames
                ? JSON.parse(service.otherFilesNames).map(name => ({
                  name,
                  url: getFileUrl(name)
                }))
                : []
            }
          };
        });
      
      setFormData(prev => ({
        ...prev,
        services: mappedServices
      }));
    }
  }, [leadDetail, options.servicesOptions]);

  // Initialize temp services when editing starts
  useEffect(() => {
    if (isEditingServices && formData.services.length > 0 && options.servicesOptions.length > 0) {
      setTempServices(formData.services.map(service => ({
        ...service,
        name: String(service.name),
        documents: service.documents || {} // Ensure documents are properly copied
      })));
    }
  }, [isEditingServices, formData.services, options.servicesOptions]);

  useEffect(() => {
    // alert(JSON.stringify(leadDetail))
    const fetchServicesOptions = async () => {
      try {
        const res = await api.get('/crm/services/options');
        setOptions(prev => ({
          ...prev,
          servicesOptions: res.data.map(opt => ({
            value: String(opt.id),
            label: opt.name
          }))
        }));
      } catch (err) {
        console.error('fetchServicesOptions error:', err);
      }
    };
    fetchServicesOptions();
    fetchIsccScopeOptions();
  }, []);

  const fetchStandardsByServiceId = async (index, serviceId) => {
    if (!serviceId) return;
    try {
      const res = await api.get(`/crm/standards/by-service/${serviceId}`);
      setStandardsOptionsMap(prev => ({
        ...prev,
        [index]: res.data.map(opt => ({
          value: opt.id,
          label: opt.name
        }))
      }));
    } catch (err) {
      console.error(`fetchStandardsByServiceId error (${serviceId}):`, err);
    }
  };

  useEffect(() => {
    if (!isEditingServices && formData.services.length > 0) {
      formData.services.forEach((service, idx) => {
        if (service.name) {
          fetchStandardsByServiceId(idx, service.name);
        }
      });
    }
    // eslint-disable-next-line
  }, [formData.services, isEditingServices]);

  // Function to get available services for a specific section (excluding already selected services)
  const getAvailableServicesForSection = (currentSectionIndex) => {
    if (!options.servicesOptions || options.servicesOptions.length === 0) {
      return [];
    }

    // Use tempServices when editing, formData.services when not editing
    const currentServices = isEditingServices ? tempServices : formData.services;

    // Get all currently selected service IDs from other sections
    const selectedServiceIds = currentServices
      .filter((_, sectionIndex) => sectionIndex !== currentSectionIndex) // Exclude current section
      .map(service => service.name || service.serviceId)
      .filter(serviceId => serviceId && serviceId !== ''); // Remove empty values

    // Filter out already selected services
    return options.servicesOptions.filter(service => 
      !selectedServiceIds.includes(service.value) && 
      !selectedServiceIds.includes(String(service.value))
    );
  };

  // Function to get the count of available services
  const getAvailableServicesCount = () => {
    return getAvailableServicesForSection(-1).length;
  };

  const addServiceSection = (e) => {
    e.preventDefault();
    setTempServices(prev => [
      ...prev,
      {
        id: undefined,
        serviceId: '',
        name: '',
        standards: [],
        scope: [],
        documents: {
          cid1: undefined,
          cid2: undefined,
          other: []
        }
      }
    ]);
  };

  const deleteServiceSection = (index) => {
    const service = tempServices[index];
    setDeleteServiceModal({
      show: true,
      serviceIndex: index,
      serviceId: service.serviceId
    });
  };

  const handleConfirmDeleteService = async () => {
    const { serviceIndex, serviceId } = deleteServiceModal;
    
    if (serviceId && lead?.id) {
      try {
        await api.delete(`/lead-services/by-lead-service`, {
          params: {
            leadId: lead.id,
            serviceId: serviceId
          }
        });
      } catch (err) {
        console.error('Gagal hapus di backend:', err);
        showAlert('danger', 'Gagal menghapus service di backend!');
        return;
      }
    }

    // Hapus dari state setelah backend berhasil
    setTempServices(prev => prev.filter((_, i) => i !== serviceIndex));
    setDeleteServiceModal({ show: false, serviceIndex: null, serviceId: null });
  };



  const handleInputChange = (fieldName, value, index) => {
    // Handle AppSelect style calls (fieldName, value) or traditional e.target style
    let name, val;
    if (typeof fieldName === 'string' && index !== undefined) {
      // AppSelect style: handleInputChange('name', value, index)
      name = fieldName;
      val = value;
    } else if (fieldName && fieldName.target) {
      // Traditional style: handleInputChange(e, index)
      name = fieldName.target.name;
      val = fieldName.target.value;
      index = value; // second parameter is index in this case
    } else {
      // Direct style: handleInputChange(fieldName, value) - need to parse field name for index
      name = fieldName;
      val = value;
      // Extract index from field name if it follows pattern like 'services-0'
      if (name && name.includes('-')) {
        const parts = name.split('-');
        if (parts.length === 2 && !isNaN(parts[1])) {
          index = parseInt(parts[1]);
          name = parts[0];
        }
      }
    }

    setTempServices(prev => {
      const updatedServices = [...prev];
      const currentService = updatedServices[index] || {};

      updatedServices[index] = {
        ...currentService,
        [name]: val,
        // Pastikan serviceId selalu sinkron dengan name untuk service field
        ...(name === 'name' ? {
          serviceId: val,
          // Reset standards & scope rows ketika service berubah
          standards: [],
          scope: []
        } : {})
      };

      return updatedServices;
    });
  };

  // ✅ standards[] and scope[] are parallel arrays, one entry per standard row
  // (added via the "Add Standard" button) so each standard keeps its own scope.
  const updateStandardRow = (sectionIndex, rowIdx, standardId) => {
    setTempServices(prev => {
      const updated = [...prev];
      const svc = { ...(updated[sectionIndex] || {}) };
      const standards = [...(svc.standards || [])];
      standards[rowIdx] = standardId;
      svc.standards = standards;
      updated[sectionIndex] = svc;
      return updated;
    });
  };

  const updateScopeRow = (sectionIndex, rowIdx, scopeValue) => {
    setTempServices(prev => {
      const updated = [...prev];
      const svc = { ...(updated[sectionIndex] || {}) };
      const scopeRows = [...(Array.isArray(svc.scope) ? svc.scope : [])];
      scopeRows[rowIdx] = scopeValue;
      svc.scope = scopeRows;
      updated[sectionIndex] = svc;
      return updated;
    });
  };

  const addStandardRow = (sectionIndex) => {
    setTempServices(prev => {
      const updated = [...prev];
      const svc = { ...(updated[sectionIndex] || {}) };
      const iscc = isIsccService(svc.serviceId || svc.name);
      svc.standards = [...(svc.standards || []), ''];
      svc.scope = [...(Array.isArray(svc.scope) ? svc.scope : []), iscc ? [] : ''];
      updated[sectionIndex] = svc;
      return updated;
    });
  };

  const removeStandardRow = (sectionIndex, rowIdx) => {
    setTempServices(prev => {
      const svc = prev[sectionIndex];
      if (!svc || (svc.standards || []).length <= 1) return prev;
      const updated = [...prev];
      updated[sectionIndex] = {
        ...svc,
        standards: svc.standards.filter((_, i) => i !== rowIdx),
        scope: (Array.isArray(svc.scope) ? svc.scope : []).filter((_, i) => i !== rowIdx)
      };
      return updated;
    });
  };


  const saveLeadService = async (service, leadId) => {
    const formData = new FormData();

    // Pastikan serviceId tersedia - gunakan name jika serviceId tidak ada
    const serviceId = service.serviceId || service.name;
    if (!serviceId) {
      throw new Error('Service ID is missing. Cannot save service without ID.');
    }

    // Build per-standard scope: parallel arrays service.standards[] and service.scope[]
    const standardIds = (Array.isArray(service.standards) ? service.standards : []).filter(Boolean);
    const scopeRows = Array.isArray(service.scope) ? service.scope : (service.scope ? [service.scope] : []);
    const standards = standardIds.map((stdId, idx) => ({
      standardId: stdId,
      scope: scopeRows[idx] !== undefined ? scopeRows[idx] : null
    }));

    // Buat objek data DTO
    const dto = {
      leadId: leadId,
      serviceId: serviceId,
      standards: standards
    };

    // Tambahkan sebagai part "data" (JSON string)
    formData.append('data', new Blob([JSON.stringify(dto)], { type: 'application/json' }));

    // Dokumen CID1
    if (service.documents?.cid1?.file instanceof File) {
      formData.append('cid1', service.documents.cid1.file);
    }

    // Dokumen CID2
    if (service.documents?.cid2?.file instanceof File) {
      formData.append('cid2', service.documents.cid2.file);
    }

    // Dokumen lainnya
    if (Array.isArray(service.documents?.other)) {
      service.documents.other.forEach((fileObj) => {
        if (fileObj.file instanceof File) {
          formData.append('other', fileObj.file); // Sesuai name backend
        }
      });
    }

    try {
      if (service.id) {
        // Service lama, update
        await api.put(`/lead-services/${service.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        // Service baru, create
        await api.post('/lead-services', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
    } catch (err) {
      console.error('Error saving lead service:', err);
      throw new Error(`Failed to save service: ${err.message}`);
    }
  };




  const handleSaveServices = async () => {
    setIsSaving(true);
    try {
      // Pre-validate dan normalize data sebelum processing
      const normalizedServices = tempServices.map((service, index) => {
        const normalizedService = { ...service };
        
        // Pastikan standards & scope selalu array (one entry per standard row)
        if (!Array.isArray(normalizedService.standards)) {
          normalizedService.standards = normalizedService.standards ? [normalizedService.standards] : [];
        }
        normalizedService.standards = normalizedService.standards.filter(Boolean);

        if (!Array.isArray(normalizedService.scope)) {
          normalizedService.scope = normalizedService.scope ? [normalizedService.scope] : [];
        }

        return normalizedService;
      });

      for (let i = 0; i < normalizedServices.length; i++) {
        const service = normalizedServices[i];

        // Validasi serviceId
        if (!service.serviceId && !service.name) {
          showAlert('warning', `Service #${i + 1}: Mohon pilih service!`);
          return;
        }

        // Pastikan serviceId tersedia
        const serviceId = service.serviceId || service.name;
        if (!serviceId) {
          showAlert('warning', `Service #${i + 1}: Service ID tidak valid!`);
          return;
        }

        // Validasi standards
        if (service.standards.length === 0) {
          showAlert('warning', `Service #${i + 1}: Mohon pilih minimal satu standard!`);
          return;
        }

        // Check scope requirements based on service type (merged across all standard rows)
        if (isIsccService(serviceId)) {
          const mergedScope = service.scope.flatMap(row => Array.isArray(row) ? row : (row ? [row] : []));
          if (mergedScope.length === 0) {
            showAlert('warning', `Service #${i + 1}: Mohon pilih minimal satu scope untuk service ISCC!`);
            return;
          }
        } else {
          const mergedScope = service.scope.filter(row => typeof row === 'string' && row.trim());
          if (mergedScope.length === 0) {
            showAlert('warning', `Service #${i + 1}: Mohon lengkapi scope untuk service!`);
            return;
          }
        }

        await saveLeadService(service, lead.id);
      }
      
      setFormData(prev => ({
        ...prev,
        services: [...normalizedServices]
      }));
      if (onUpdateServices) {
        onUpdateServices(normalizedServices);
      }
      setIsEditingServices(false);
      // Activity History is part of the lead detail response, so it needs its own refetch —
      // otherwise the entries for this edit only appear after a full page reload.
      if (onRefreshLeadDetail) {
        await onRefreshLeadDetail();
      }
      showAlert('success', 'Services saved successfully!');
    } catch (err) {
      showAlert('danger', `Failed to save services: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };


  const handleCancelEdit = (e) => {
    e.preventDefault();
    setTempServices([...formData.services]);
    setIsEditingServices(false);
  };

  const handleDeleteFile = (fileName) => {
    setDeleteFileModal({
      show: true,
      fileName: fileName
    });
  };

  const handleConfirmDeleteFile = async () => {
    const { fileName } = deleteFileModal;

    setIsDeletingFile(true);
    try {
      await api.delete(`/lead-services/lead-docs/${fileName}`);

      showAlert('success', 'File deleted successfully from storage and database');

      // Refresh lead detail from server so document list is in sync
      if (onRefreshLeadDetail) {
        await onRefreshLeadDetail();
      } else {
        // Fallback: update local state only
        const removeFromDocs = (documents) => {
          const updated = { ...documents };
          if (updated.cid1?.name === fileName) delete updated.cid1;
          if (updated.cid2?.name === fileName) delete updated.cid2;
          if (Array.isArray(updated.other)) {
            updated.other = updated.other.filter(doc => doc.name !== fileName);
          }
          return updated;
        };

        setFormData(prev => ({
          ...prev,
          services: prev.services.map(s => ({ ...s, documents: removeFromDocs(s.documents) }))
        }));

        if (isEditingServices) {
          setTempServices(prev => prev.map(s => ({ ...s, documents: removeFromDocs(s.documents) })));
        }
      }
    } catch (error) {
      console.error("Gagal menghapus file:", error);
      showAlert('danger', 'Gagal menghapus file dari server.');
    } finally {
      setIsDeletingFile(false);
      setDeleteFileModal({ show: false, fileName: null });
    }
  };

  const renderDocumentSection = (service, sectionIndex, isEditing) => {
    // Get documents from the correct source based on editing mode
    const currentServices = isEditing ? tempServices : formData.services;
    const currentService = currentServices[sectionIndex] || service;
    const documents = currentService.documents || {};

    const handleDocumentUpload = (type) => (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        if (isEditing) {
          // When editing, update tempServices
          setTempServices(prev => {
            const updatedServices = [...prev];
            const currentService = updatedServices[sectionIndex] || {};
            updatedServices[sectionIndex] = {
              ...currentService,
              documents: {
                ...(currentService.documents || {}),
                [type]: {
                  name: file.name,
                  size: `${(file.size / 1024).toFixed(2)} KB`,
                  url: URL.createObjectURL(file),
                  file: file // simpan file asli
                }
              }
            };
            return updatedServices;
          });
        } else {
          // When not editing, update formData.services
          const updatedService = {
            ...formData.services[sectionIndex],
            documents: {
              ...(formData.services[sectionIndex].documents || {}),
              [type]: {
                name: file.name,
                size: `${(file.size / 1024).toFixed(2)} KB`,
                url: URL.createObjectURL(file),
                file: file // simpan file asli
              }
            }
          };

          const updatedServices = [...formData.services];
          updatedServices[sectionIndex] = updatedService;

          setFormData(prev => ({
            ...prev,
            services: updatedServices
          }));

          if (onUpdateServices) {
            onUpdateServices(updatedServices);
          }
        }
      }
    };

    const handleOtherDocumentsUpload = (acceptedFiles) => {
      const newFiles = acceptedFiles.map(file => ({
        name: file.name,
        size: `${(file.size / 1024).toFixed(2)} KB`,
        url: URL.createObjectURL(file),
        file: file // simpan file asli
      }));

      if (isEditing) {
        // When editing, update tempServices
        setTempServices(prev => {
          const updatedServices = [...prev];
          const currentService = updatedServices[sectionIndex] || {};
          const currentDocuments = currentService.documents || {};
          updatedServices[sectionIndex] = {
            ...currentService,
            documents: {
              ...currentDocuments,
              other: [...(currentDocuments.other || []), ...newFiles]
            }
          };
          return updatedServices;
        });
      } else {
        // When not editing, update formData.services
        const updatedService = {
          ...formData.services[sectionIndex],
          documents: {
            ...(formData.services[sectionIndex].documents || {}),
            other: [...(documents.other || []), ...newFiles]
          }
        };

        const updatedServices = [...formData.services];
        updatedServices[sectionIndex] = updatedService;

        setFormData(prev => ({
          ...prev,
          services: updatedServices
        }));

        if (onUpdateServices) {
          onUpdateServices(updatedServices);
        }
      }
    };



    // Check if this is a training service
    const serviceId = currentService.serviceId || currentService.name;
    const isTraining = isTrainingService(serviceId);
    const isSustainability = isIsccService(serviceId);

    return (
      <div className="mt-3">
        <label className="form-label">
          Project Documents
        </label>
        <div className="row g-2">
          {/* Only show CID1 and CID2 for non-training services */}
          {!isTraining && (
            <>
              <div className="col-12 col-sm-4">
                <DocumentSection
                  title={getSalesPackCidCode(isSustainability, 1)}
                  documents={documents.cid1 ? [documents.cid1] : []}
                  onUpload={handleDocumentUpload('cid1')}
                  onDelete={handleDeleteFile}
                  maxFiles={1}
                  readOnly={!isEditing}
                />
                {/* {documents.cid1 && documents.cid1.name && (
                  <div>
                    <a href={getFileUrl(documents.cid1.name)} target="_blank" rel="noopener noreferrer">
                      Download CID1: {documents.cid1.name}
                    </a>
                  </div>
                )} */}
              </div>
              <div className="col-12 col-sm-4">
                <DocumentSection
                  title={getSalesPackCidCode(isSustainability, 2)}
                  documents={documents.cid2 ? [documents.cid2] : []}
                  onUpload={handleDocumentUpload('cid2')}
                  onDelete={handleDeleteFile}
                  maxFiles={1}
                  readOnly={!isEditing}
                />
                {/* {documents.cid2 && documents.cid2.name && (
                  <div>
                    <a href={getFileUrl(documents.cid2.name)} target="_blank" rel="noopener noreferrer">
                      Download CID2: {documents.cid2.name}
                    </a>
                  </div>
                )} */}
              </div>
            </>
          )}
          <div className={`col-12 ${!isTraining ? 'col-sm-4' : 'col-sm-12'}`}>
            <DocumentSection
              title={`Other${isTraining ? ' (Required)' : ' (Optional)'}`}
              documents={documents.other || []}
              onUpload={handleOtherDocumentsUpload}
              maxFiles={10}
              onDelete={handleDeleteFile}
              readOnly={!isEditing}
            />
            {Array.isArray(documents.other) && documents.other.length > 0 && !isEditing && (
              <ul>
                {/* {documents.other.map((doc, idx) => (
                  <li key={idx}>
                    <a
                      href={getFileUrl(doc.name)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {doc.name}
                    </a>
                  </li>
                ))} */}
              </ul>
            )}
          </div>
        </div>
      </div>
    );
  };


  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading lead details...</span>
        </div>
      </div>
    );
  }
  if (!lead) {
    return <div>No lead data found.</div>;
  }
  
  // Show loading when leadDetail is still being fetched
  if (!leadDetail) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading lead details...</span>
        </div>
      </div>
    );
  }



  const getFileUrl = (fileName) => {
    if (!fileName) return null;

    const baseUrl = process.env.REACT_APP_API_ENDPOINT || '';
    return `${baseUrl}/api/public/displaypdf-leads/${fileName}`;
  };


  const handleConvertToProposal = async (e) => {
    e.preventDefault();

    if (formData.services.length === 0) {
      showAlert('warning', 'Please add at least one service before converting to proposal');
      return;
    }

    // Guard against race condition: isTrainingService() relies on options.servicesOptions,
    // which is fetched asynchronously on mount. If the user clicks "Convert to Proposal"
    // before that fetch resolves, training detection silently returns false/undefined and
    // the document validation below makes wrong decisions (only fixed after a refresh,
    // when servicesOptions is already populated).
    if (!options.servicesOptions || options.servicesOptions.length === 0) {
      showAlert('warning', 'Still loading service data, please try again in a moment.');
      return;
    }

    try {
      for (const service of formData.services) {
        const serviceId = service.serviceId || service.id; // sesuaikan properti ID-nya
        const res = await api.get('/lead-services/check-documents', {
          params: {
            leadId: lead.id,
            serviceId,
          },
        });

        const { cid1Exists, cid2Exists, otherExists } = res.data;

        // Check if this is a training service
        const isTraining = isTrainingService(serviceId);
        
        if (isTraining) {
          // For training services, only "Other" documents are mandatory
          const hasOtherDocuments = service.documents?.other && Array.isArray(service.documents.other) && service.documents.other.length > 0;
          
          if (!hasOtherDocuments) {
            showAlert('warning',
              `Dokumen belum lengkap untuk Training Service:\n` +
              `- Service ID: ${serviceId}\n` +
              `- Other Documents: ${hasOtherDocuments ? '✅ Ada' : '❌ Tidak Ada'}\n` +
              `Note: Training services only require "Other" documents`
            );
            return;
          }
        }
        // For non-training services, CID1 and CID2 are now OPTIONAL
        

      }

      // ✅ TAMBAHAN: Simpan data lead services ke localStorage sebelum navigate
      const leadServicesData = formData.services.map(service => ({
        id: service.id,
        serviceId: service.serviceId || service.id,
        name: service.name,
        standards: service.standards || [],
        scope: service.scope || '',
        documents: service.documents || {}
      }));
      
      // Simpan ke localStorage dengan key yang unik berdasarkan lead ID
      localStorage.setItem(`leadServices_${lead.id}`, JSON.stringify(leadServicesData));
      
      // Semua file lengkap
      navigate(`/proposals/edit/${lead.id}`);
    } catch (error) {
      console.error('Gagal memeriksa dokumen:', error);
      showAlert('danger', 'Terjadi kesalahan saat memeriksa dokumen.');
    }
  };


  return (
    <div className="row g-3">
      {/* Custom Alert Component - Positioned at top like in Prospects */}
      {alert.show && (
        <div className="col-12">
          <Alert
            type={alert.type}
            message={alert.message}
            onClose={hideAlert}
          />
        </div>
      )}
      <div className="col-12 col-lg-8">
        {/* Company Section */}
        <div className="mb-3">
          <h2 className='modal-title mb-3'>Company</h2>
          <div className="row g-2">
            <div className="col-12 col-sm-6">
              <div className="mb-3">
                <label className="form-label">Client</label>
                <input type="text" className="form-control" value={lead.companyName || ''} disabled style={{ backgroundColor: "#f5f7fb" }} />
              </div>
            </div>
            <div className="col-12 col-sm-6">
              <div className="mb-3">
                <label className="form-label">Contact Name</label>
                <input type="text" className="form-control" value={lead.pic || ''} disabled style={{ backgroundColor: "#f5f7fb" }} />
              </div>
            </div>
            <div className="col-12 col-sm-6">
              <div className="mb-3">
                <label className="form-label">Contact Phone Number</label>
                <input type="text" className="form-control" value={lead?.contactNumber || ''} disabled style={{ backgroundColor: "#f5f7fb" }} />
              </div>
            </div>
            <div className="col-12 col-sm-6">
              <div className="mb-3">
                <label className="form-label">Contact Email</label>
                <input type="email" className="form-control" value={lead.email || ''} disabled style={{ backgroundColor: "#f5f7fb" }} />
              </div>
            </div>
            <div className="col-12 col-sm-6">
              <div className="mb-3">
                <label className="form-label">Address</label>
                <textarea className="form-control" value={lead.address || ''} disabled style={{ backgroundColor: "#f5f7fb" }} rows="3" />
              </div>
            </div>
            <div className="col-12 col-sm-6">
              <div className="mb-3">
                <label className="form-label">Company Phone Number</label>
                <input type="text" className="form-control" value={lead?.companyPhoneNumber || ''} disabled style={{ backgroundColor: "#f5f7fb" }} />
              </div>
            </div>
            <div className="col-12 col-sm-6">
              <div className="mb-3">
                <label className="form-label">Sector</label>
                <input type="text" className="form-control" value={lead.sector || ''} disabled style={{ backgroundColor: "#f5f7fb" }} />
              </div>
            </div>
            <div className="col-12 col-sm-6">
              <div className="mb-3">
                <label className="form-label">Company Website</label>
                <input type="text" className="form-control" value={lead.website || ''} disabled style={{ backgroundColor: "#f5f7fb" }} />
              </div>
            </div>
          </div>
        </div>

        {/* Project Info Section */}
        <div className="mb-3">
          <h2 className='modal-title mb-3'>Project Info</h2>
          <div className="row g-2">
            <div className="col-12 col-sm-6">
              <div className="mb-3">
                <label className="form-label">Stage</label>
                <input type="text" className="form-control" value={lead.stage || ''} disabled style={{ backgroundColor: "#f5f7fb" }} />
              </div>
            </div>
            <div className="col-12 col-sm-6">
              <div className="mb-3">
                <label className="form-label">Source</label>
                <input type="text" className="form-control" value={lead.leadSource || ''} disabled style={{ backgroundColor: "#f5f7fb" }} />
              </div>
            </div>
            <div className="col-12 col-sm-6">
              <div className="mb-3">
                <label className="form-label">Project Name</label>
                <input type="text" className="form-control" value={lead.projectName || ''} disabled style={{ backgroundColor: "#f5f7fb" }} />
              </div>
            </div>
            <div className="col-12 col-sm-6">
              <div className="mb-3">
                <label className="form-label">Project Description</label>
                <textarea className="form-control" value={lead.description || ''} disabled style={{ backgroundColor: "#f5f7fb" }} rows="3" />
              </div>
            </div>
            <div className="col-12 col-sm-6">
              <div className="mb-3">
                <label className="form-label">Assign User</label>
                <input 
                  type="text" 
                  className="form-control" 
                  value={
                    (lead.assignedToFirstName && lead.assignedToLastName) 
                      ? `${lead.assignedToFirstName} ${lead.assignedToLastName}`.trim()
                      : (lead.assignedTo?.firstName && lead.assignedTo?.lastName)
                      ? `${lead.assignedTo.firstName} ${lead.assignedTo.lastName}`.trim()
                      : lead.assignUser || ''
                  } 
                  disabled 
                  style={{ backgroundColor: "#f5f7fb" }} 
                />
              </div>
            </div>
          </div>
        </div>

        {/* Break line between sections */}
        <hr className="my-4 border-secondary" />

        {/* Product and Services Section */}
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2 className='modal-title'>Product and Services</h2>
            <div className="d-flex align-items-center">
              {!isEditingServices && (
                <>
                  <a href="#" className="btn btn-primary me-2" onClick={(e) => {
                    e.preventDefault();
                    setIsEditingServices(true);
                  }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                      viewBox="0 0 24 24" fill="none" stroke="currentColor"
                      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                      className="icon icon-2 me-1">
                      <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1"></path>
                      <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z"></path>
                      <path d="M16 5l3 3"></path>
                    </svg>
                    Edit Services
                  </a>
                  <a href='#' className="btn btn-info" onClick={handleConvertToProposal}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                      viewBox="0 0 24 24" fill="none" stroke="currentColor"
                      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                      className="icon icon-2 me-1">
                      <path d="M14 3v4a1 1 0 0 0 1 1h4"></path>
                      <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z"></path>
                      <path d="M9 17h6"></path>
                      <path d="M9 13h6"></path>
                    </svg>
                    Convert to Proposal
                  </a>
                </>
              )}
            </div>
          </div>
          {(isEditingServices ? tempServices : formData.services).map((service, sectionIndex) => (
            <div key={sectionIndex} className="card mb-4">
              <div className="card-header">
                <div className="d-flex justify-content-between align-items-center">
                  <h4 className="card-title mb-0">Service #{sectionIndex + 1}</h4>
                </div>
              </div>
              <div className="card-body">
              <div className="row g-2">
                <div className="col-12 col-sm-6">
                  <AppSelect
                    name={`services-${sectionIndex}`}
                    label="Services"
                    value={service.name || ''}
                                         onChange={(fieldName, value) => {
                       handleInputChange('name', value, sectionIndex);
                       fetchStandardsByServiceId(sectionIndex, value);
                     }}
                    options={(() => {
                      const availableServices = getAvailableServicesForSection(sectionIndex);
                      // If current service is selected, make sure it's included in options
                      if (service.name && !availableServices.find(opt => opt.value === service.name)) {
                        const currentServiceOption = options.servicesOptions.find(opt => opt.value === service.name);
                        if (currentServiceOption) {
                          return [currentServiceOption, ...availableServices];
                        }
                      }
                      return availableServices;
                    })()}
                    placeholder="Select services"
                    required={true}
                    disabled={!isEditingServices}
                    valueField="value"
                    labelField="label"
                    searchField="label"
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">
                    Standards
                    {isIsccService(service.name) && <span className="text-info ms-2">(ISCC Service - Multiple Scope Selection Available)</span>}
                  </label>
                </div>
                {(() => {
                  const standardRows = (service.standards && service.standards.length > 0) ? service.standards : [''];
                  const scopeRows = Array.isArray(service.scope) ? service.scope : [];
                  const iscc = isIsccService(service.name);
                  const selectedElsewhere = (rowIdx) => standardRows.filter((id, i) => i !== rowIdx && id);

                  return standardRows.map((standardId, rowIdx) => (
                    <div className="col-12 mb-3" key={rowIdx}>
                      <div className="p-3 border rounded bg-light">
                        <div className="row g-2 align-items-start">
                          <div className="col">
                            <AppSelect
                              name={`standards-${sectionIndex}-${rowIdx}`}
                              value={standardId || ''}
                              onChange={(fieldName, value) => updateStandardRow(sectionIndex, rowIdx, value)}
                              options={(standardsOptionsMap[sectionIndex] || []).filter(opt => !selectedElsewhere(rowIdx).includes(opt.value))}
                              placeholder="Select standard"
                              required={rowIdx === 0}
                              disabled={!isEditingServices}
                              valueField="value"
                              labelField="label"
                              searchField="label"
                            />
                          </div>
                          {isEditingServices && standardRows.length > 1 && (
                            <div className="col-auto">
                              <button
                                type="button"
                                className="btn btn-icon btn-ghost-danger"
                                onClick={() => removeStandardRow(sectionIndex, rowIdx)}
                                title="Remove standard"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-trash" width="20" height="20" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                  <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                  <path d="M4 7l16 0"></path>
                                  <path d="M10 11l0 6"></path>
                                  <path d="M14 11l0 6"></path>
                                  <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12"></path>
                                  <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3"></path>
                                </svg>
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="row g-2 mt-2">
                          <div className="col-12">
                            {iscc ? (
                              <AppSelect
                                name={`scope-${sectionIndex}-${rowIdx}`}
                                value={Array.isArray(scopeRows[rowIdx]) ? scopeRows[rowIdx] : (scopeRows[rowIdx] ? [scopeRows[rowIdx]] : [])}
                                onChange={(fieldName, value) => updateScopeRow(sectionIndex, rowIdx, Array.isArray(value) ? value : (value ? [value] : []))}
                                options={isccScopeOptions}
                                placeholder="Select scope items"
                                disabled={!isEditingServices}
                                multiple={true}
                                valueField="value"
                                labelField="label"
                                searchField="label"
                              />
                            ) : (
                              <textarea
                                className="form-control"
                                value={typeof scopeRows[rowIdx] === 'string' ? scopeRows[rowIdx] : ''}
                                onChange={(e) => updateScopeRow(sectionIndex, rowIdx, e.target.value)}
                                placeholder="Enter scope for this standard"
                                rows="2"
                                readOnly={!isEditingServices}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ));
                })()}
                {isEditingServices && (
                  <div className="col-12 d-flex justify-content-end mt-1">
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={() => addStandardRow(sectionIndex)}
                      disabled={!standardsOptionsMap[sectionIndex] || standardsOptionsMap[sectionIndex].length === 0}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                        <path d="M12 5l0 14"></path>
                        <path d="M5 12l14 0"></path>
                      </svg>
                      Add Standard
                    </button>
                  </div>
                )}
              </div>
              {/* Document Section per Service */}
              {renderDocumentSection(service, sectionIndex, isEditingServices)}
              {isEditingServices ? (
                <div className="text-end mt-3">
                  <a
                    href="#"
                    className="btn btn-danger"
                    onClick={(e) => {
                      e.preventDefault();
                      deleteServiceSection(sectionIndex);
                    }}
                  >
                    Delete
                  </a>
                </div>
              ) : null}
              </div>
            </div>
          ))}
          {(isEditingServices ? tempServices : formData.services).length === 0 && (
            <div className="text-center text-muted py-3">No services added</div>
          )}
          {/* Add Services button and Cancel/Save buttons at bottom */}
          {isEditingServices && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <button 
                type="button" 
                className="btn btn-primary" 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  addServiceSection(e);
                }}
                disabled={getAvailableServicesCount() === 0}
                title={getAvailableServicesCount() === 0 ? "All services are already selected" : `${getAvailableServicesCount()} service(s) available to add`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                  viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  className="icon icon-2 me-1">
                  <path d="M12 5l0 14"></path>
                  <path d="M5 12l14 0"></path>
                </svg>
                Add Services
              </button>
              <div className="d-flex align-items-center">
                <a href="#" className="btn btn-secondary me-2" onClick={handleCancelEdit}>
                  Cancel
                </a>
                <a href="#" className="btn btn-success" onClick={handleSaveServices}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-2 me-1">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                    <path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0"></path>
                    <path d="M9 12l2 2l4 -4"></path>
                  </svg>
                  Save Changes
                </a>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* History Sections */}
      <div className="col-12 col-lg-4">
        <h2 className='modal-title mb-3'>Follow Up History</h2>
        <div className="card mb-3">
          <div className="card-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <div className="activity-timeline">
              {history?.followUps?.length ? history.followUps.map((followUp, index) => (
                <div key={index} className="activity-item">
                  <div className="row g-0">
                    <div className="col-auto position-relative">
                      <div className={`activity-icon bg-${getFollowUpActionColor(followUp.action, followUp.type)} text-white rounded-circle d-flex align-items-center justify-content-center`}
                        style={{
                          width: '24px',
                          height: '24px',
                          position: 'relative',
                          zIndex: 2
                        }}>
                         {getFollowUpIcon(followUp.type)}
                      </div>
                    </div>
                    <div className="col ms-3">
                      <div>
                        <div className="d-flex align-items-center justify-content-between mb-1">
                          <div className="activity-title text-primary me-2">
                            {followUp.subject}
                          </div>
                          <div className="activity-time text-muted small">
                            {formatActivityDate(followUp.date + 'T' + followUp.time)}
                          </div>
                        </div>
                        <div className="activity-description text-muted">
                          <span className="fw-bold">{getFollowUpPerformer(followUp)}</span>{' '}
                          {getFollowUpActionText(followUp.action)} {followUp.type ? followUp.type.toLowerCase() : 'task'}
                          {followUp.contactedPerson && (
                            <> with <span className="fw-bold">{followUp.contactedPerson}</span></>
                          )}
                        </div>
                        {/* Changed fields, one line each ("Status: Open → Completed") */}
                        {followUp.changes && (
                          <ul className="activity-notes text-muted small mt-1 mb-0 ps-3">
                            {followUp.changes.split('\n').filter(Boolean).map((change, changeIndex) => (
                              <li key={changeIndex}>{change}</li>
                            ))}
                          </ul>
                        )}
                        {!followUp.changes && followUp.notes && (
                          <div className="activity-notes text-muted small mt-1">
                            {followUp.notes}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )) : (
                  <div className="text-center text-muted py-3">No follow up history</div>
                )}
            </div>
          </div>
        </div>
        <h2 className='modal-title mb-3'>Activity History (Log)</h2>
        <div className="card">
          <div className="card-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <div className="activity-timeline">
              {history?.activityHistory?.length ? history.activityHistory.map((activity, index) => (
                <div key={index} className="activity-item">
                  <div className="row g-0">
                    <div className="col-auto position-relative">
                      <div className={`activity-icon bg-${getActivityColor(activity.type)} text-white rounded-circle d-flex align-items-center justify-content-center`}
                        style={{
                          width: '24px',
                          height: '24px',
                          position: 'relative',
                          zIndex: 2
                        }}>
                        {getActivityIcon(activity.type, activity.notes)}
                      </div>
                    </div>
                    <div className="col ms-3">
                      <div>
                        <div className="d-flex align-items-center justify-content-between mb-1">
                          <div className={`activity-title text-${getActivityColor(activity.type)}`}>
                            {getActivityTitle(activity.type, activity.notes)}
                          </div>
                          <div className="activity-time text-muted small">
                            {formatActivityDate(activity.date)}
                          </div>
                        </div>
                        <div className="activity-description text-muted">
                          <span className="fw-bold">
                            {(activity.userFirstName && activity.userLastName)
                              ? `${activity.userFirstName} ${activity.userLastName}`.trim()
                              : activity.user || 'Unknown'}
                          </span> {getActivitySummary(activity.notes)}
                        </div>
                        {/* Changed fields, one line each ("Stage: Prospect → Negotiation") */}
                        {getActivityChanges(activity.notes).length > 0 && (
                          <ul className="activity-notes text-muted small mt-1 mb-0 ps-3">
                            {getActivityChanges(activity.notes).map((change, changeIndex) => (
                              <li key={changeIndex}>{change}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )) : (
                  <div className="text-center text-muted py-3">No activity history</div>
                )}
            </div>
          </div>
        </div>
       </div>

       {/* Confirmation Modals */}
       <ConfirmationModal
         isOpen={deleteServiceModal.show}
         onClose={() => setDeleteServiceModal({ show: false, serviceIndex: null, serviceId: null })}
         onConfirm={handleConfirmDeleteService}
         title="Delete Service"
         message="Are you sure you want to delete this? This action cannot be undone."
         confirmText="Delete"
         cancelText="Cancel"
         confirmVariant="danger"
         modalId="delete-service-modal"
       />
       
       <ConfirmationModal
         isOpen={deleteFileModal.show}
         onClose={() => { if (!isDeletingFile) setDeleteFileModal({ show: false, fileName: null }); }}
         onConfirm={handleConfirmDeleteFile}
         title="Delete File"
         message="Are you sure you want to delete this? This action cannot be undone."
         confirmText="Delete"
         cancelText="Cancel"
         confirmVariant="danger"
         modalId="delete-file-modal"
         isLoading={isDeletingFile}
       />

     </div>
   );
 };

const getActivityIcon = (type, notes) => {
  // Normalize type to uppercase for consistency
  const normalizedType = type ? type.toUpperCase() : '';
  
  // ✅ Handle CREATE/creation type
  if (normalizedType === 'CREATE' || normalizedType === 'CREATION') {
    if (notes && notes.toLowerCase().includes('with project')) {
      // Icon for "Lead created with project"
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-folder-plus" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
          <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
          <path d="M12 19h-7a2 2 0 0 1 -2 -2v-11a2 2 0 0 1 2 -2h4l3 3h7a2 2 0 0 1 2 2v3.5"></path>
          <path d="M16 19h6"></path>
          <path d="M19 16v6"></path>
        </svg>
      );
    } else {
      // Icon for general "Lead Created"
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-plus" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
          <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
          <path d="M12 5l0 14"></path>
          <path d="M5 12l14 0"></path>
        </svg>
      );
    }
  }
  
  // ✅ Handle UPDATE type
  if (normalizedType === 'UPDATE') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-pencil" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
        <path d="M4 20h4l10.5 -10.5a2.828 2.828 0 1 0 -4 -4l-10.5 10.5v4"></path>
        <path d="M13.5 6.5l4 4"></path>
      </svg>
    );
  }
  
  // ✅ Handle ASSIGNMENT type
  if (normalizedType === 'ASSIGNMENT' || normalizedType === 'ASSIGN') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-user-check" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
        <path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0"></path>
        <path d="M6 21v-2a4 4 0 0 1 4 -4h4"></path>
        <path d="M15 19l2 2l4 -4"></path>
      </svg>
    );
  }
  
  // ✅ Handle STATUS_CHANGE/CHANGE_STAGE type
  if (normalizedType === 'STATUS_CHANGE' || normalizedType === 'CHANGE_STAGE') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-refresh" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
        <path d="M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4"></path>
        <path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4"></path>
      </svg>
    );
  }
  
  // ✅ Handle DELETE type
  if (normalizedType === 'DELETE') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-trash" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
        <path d="M4 7l16 0"></path>
        <path d="M10 11l0 6"></path>
        <path d="M14 11l0 6"></path>
        <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12"></path>
        <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3"></path>
      </svg>
    );
  }
  
  // ✅ Handle VIEW/VIEW_LEAD_DETAIL type
  if (normalizedType === 'VIEW' || normalizedType === 'VIEW_LEAD_DETAIL') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-eye" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
        <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0"></path>
        <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6"></path>
      </svg>
    );
  }
  
  // ✅ Handle DOWNLOAD_FILE type
  if (normalizedType.includes('DOWNLOAD')) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-download" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
        <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2"></path>
        <path d="M7 11l5 5l5 -5"></path>
        <path d="M12 4l0 12"></path>
      </svg>
    );
  }
  
  // ✅ Handle COMMENT/NOTE type
  if (normalizedType === 'COMMENT' || normalizedType === 'NOTE') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-message" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
        <path d="M8 9h8"></path>
        <path d="M8 13h6"></path>
        <path d="M18 4a3 3 0 0 1 3 3v8a3 3 0 0 1 -3 3h-5l-5 3v-3h-2a3 3 0 0 1 -3 -3v-8a3 3 0 0 1 3 -3h12z"></path>
      </svg>
    );
  }
  
  // Default icon for unknown types
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-point" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
      <circle cx="12" cy="12" r="4"></circle>
    </svg>
  );
};

const getActivityTitle = (type, notes) => {
  // Normalize type to uppercase for consistency
  const normalizedType = type ? type.toUpperCase() : '';
  
  // ✅ Handle CREATE/creation type
  if (normalizedType === 'CREATE' || normalizedType === 'CREATION') {
    if (notes && notes.toLowerCase().includes('with project')) {
      return 'Lead Created with Project';
    } else {
      return 'Lead Created';
    }
  }
  
  // ✅ Handle UPDATE type
  if (normalizedType === 'UPDATE') {
    return 'Lead Updated';
  }
  
  // ✅ Handle ASSIGNMENT type
  if (normalizedType === 'ASSIGNMENT' || normalizedType === 'ASSIGN') {
    return 'Lead Assigned';
  }
  
  // ✅ Handle STATUS_CHANGE/CHANGE_STAGE type
  if (normalizedType === 'STATUS_CHANGE' || normalizedType === 'CHANGE_STAGE') {
    return 'Stage Changed';
  }
  
  // ✅ Handle DELETE type
  if (normalizedType === 'DELETE') {
    return 'Lead Deleted';
  }
  
  // ✅ Handle VIEW type
  if (normalizedType === 'VIEW' || normalizedType === 'VIEW_LEAD_DETAIL') {
    return 'Lead Viewed';
  }
  
  // ✅ Handle DOWNLOAD_FILE type
  if (normalizedType.includes('DOWNLOAD')) {
    return 'File Downloaded';
  }
  
  // ✅ Handle COMMENT/NOTE type
  if (normalizedType === 'COMMENT' || normalizedType === 'NOTE') {
    return 'Comment Added';
  }
  
  // Default title for unknown types - capitalize first letter
  if (type) {
    return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase().replace(/_/g, ' ');
  }
  
  return 'Activity';
};

const getActivityColor = (type) => {
  // Normalize type to uppercase for consistency
  const normalizedType = type ? type.toUpperCase() : '';
  
  // ✅ CREATE/creation - Blue (Primary)
  if (normalizedType === 'CREATE' || normalizedType === 'CREATION') {
    return 'primary';
  }
  
  // ✅ UPDATE - Info (Cyan/Teal)
  if (normalizedType === 'UPDATE') {
    return 'info';
  }
  
  // ✅ ASSIGNMENT - Green (Success)
  if (normalizedType === 'ASSIGNMENT' || normalizedType === 'ASSIGN') {
    return 'success';
  }
  
  // ✅ STATUS_CHANGE/CHANGE_STAGE - Orange (Warning)
  if (normalizedType === 'STATUS_CHANGE' || normalizedType === 'CHANGE_STAGE') {
    return 'warning';
  }
  
  // ✅ DELETE - Red (Danger)
  if (normalizedType === 'DELETE') {
    return 'danger';
  }
  
  // ✅ VIEW - Purple
  if (normalizedType === 'VIEW' || normalizedType === 'VIEW_LEAD_DETAIL') {
    return 'purple';
  }
  
  // ✅ DOWNLOAD - Indigo
  if (normalizedType.includes('DOWNLOAD')) {
    return 'indigo';
  }
  
  // ✅ COMMENT/NOTE - Azure
  if (normalizedType === 'COMMENT' || normalizedType === 'NOTE') {
    return 'azure';
  }
  
  // Default - Gray (Secondary)
  return 'secondary';
};

const formatActivityDate = (dateString) => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return dateString; // Return original if invalid
    }
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString; // Return original if error
  }
};

// An activity's notes are a summary line, optionally followed by one changed field per line.
const getActivitySummary = (notes) => (notes ? notes.split('\n')[0] : '');

const getActivityChanges = (notes) => (notes ? notes.split('\n').slice(1).filter(Boolean) : []);

// Follow Up History entries are task events: who did it, and what they changed.
const getFollowUpPerformer = (followUp) => {
  const fullName = `${followUp.performedByFirstName || ''} ${followUp.performedByLastName || ''}`.trim();
  if (fullName) return fullName;
  return followUp.performedByName || followUp.assignedUserName || 'Unknown';
};

const getFollowUpActionText = (action) => {
  switch ((action || '').toUpperCase()) {
    case 'UPDATED':
      return 'updated';
    case 'DELETED':
      return 'deleted';
    case 'CREATED':
      return 'created';
    default:
      // Entries written before the task history existed only recorded the creation.
      return 'logged';
  }
};

const getFollowUpActionColor = (action, type) => {
  switch ((action || '').toUpperCase()) {
    case 'UPDATED':
      return 'info';
    case 'DELETED':
      return 'danger';
    default:
      return getFollowUpIconColor(type);
  }
};

const getFollowUpIcon = (type) => {
  const normalizedType = type ? type.toUpperCase() : '';
  
  if (normalizedType === 'CALL' || normalizedType === 'PHONE') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-phone" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
        <path d="M5 4h4l2 5l-2.5 1.5a11 11 0 0 0 5 5l1.5 -2.5l5 2v4a2 2 0 0 1 -2 2a16 16 0 0 1 -15 -15a2 2 0 0 1 2 -2"></path>
      </svg>
    );
  }
  
  if (normalizedType === 'MEETING') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-users" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
        <path d="M9 7m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0"></path>
        <path d="M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2"></path>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
        <path d="M21 21v-2a4 4 0 0 0 -3 -3.85"></path>
      </svg>
    );
  }
  
  if (normalizedType === 'EMAIL') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-mail" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
        <path d="M3 7a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-10z"></path>
        <path d="M3 7l9 6l9 -6"></path>
      </svg>
    );
  }
  
  if (normalizedType === 'TODO' || normalizedType === 'TASK') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-checkbox" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
        <path d="M9 11l3 3l8 -8"></path>
        <path d="M20 12v6a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2h9"></path>
      </svg>
    );
  }
  
  // Default icon
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-checkbox" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
      <path d="M9 11l3 3l8 -8"></path>
      <path d="M20 12v6a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2h9"></path>
    </svg>
  );
};

const getFollowUpIconColor = (type) => {
  const normalizedType = type ? type.toUpperCase() : '';
  
  if (normalizedType === 'CALL' || normalizedType === 'PHONE') {
    return 'primary'; // Blue
  }
  
  if (normalizedType === 'MEETING') {
    return 'success'; // Green
  }
  
  if (normalizedType === 'EMAIL') {
    return 'info'; // Cyan
  }
  
  if (normalizedType === 'TODO' || normalizedType === 'TASK') {
    return 'warning'; // Orange
  }
  
  return 'secondary'; // Gray - default
};

const getStageColor = (stage) => {
  switch (stage?.toLowerCase()) {
    case 'new':
      return 'blue';
    case 'qualified':
      return 'green';
    case 'proposition':
      return 'yellow';
    case 'negotiation':
      return 'orange';
    case 'won':
      return 'success';
    case 'lost':
      return 'danger';
    default:
      return 'secondary';
  }
};

export default DetailTab;
